import { CheckIcon, EyeIcon, EyeOffIcon, XIcon } from "lucide-react";
import { useId, useMemo } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";

type ComponentProps = {
	password: string;
	setPassword: (value: string) => void;
	isVisible: boolean;
	toggleVisibility: () => void;
};

export const NewPasswordComponent = ({
	password,
	setPassword,
	isVisible,
	toggleVisibility,
}: ComponentProps) => {
	const id = useId();

	const checkStrength = (pass: string) => {
		const requirements = [
			{ regex: /.{8,}/, text: "Au moins 8 caractères" },
			{ regex: /[0-9]/, text: "Au moins 1 chiffre" },
			{ regex: /[a-z]/, text: "Au moins 1 minuscule" },
			{ regex: /[A-Z]/, text: "Au moins 1 majuscule" },
			{ regex: /[!@#$%^&*(),.?":{}|<>]/, text: "Au moins 1 caractère spécial" },
		];

		return requirements.map((req) => ({
			met: req.regex.test(pass),
			text: req.text,
		}));
	};

	const strength = checkStrength(password);

	const strengthScore = useMemo(() => {
		return strength.filter((req) => req.met).length;
	}, [strength]);

	const getStrengthColor = (score: number) => {
		if (score === 0) return "bg-border";
		if (score <= 1) return "bg-red-500";
		if (score <= 2) return "bg-orange-500";
		if (score === 3) return "bg-amber-500";
		return "bg-emerald-500";
	};

	const getStrengthText = (score: number) => {
		if (score === 0) return "Saisir un mot de passe";
		if (score <= 2) return "Mot de passe faible";
		if (score === 3) return "Mot de passe moyen";
		return "Mot de passe fort";
	};

	return (
		<div>
			<Label className="flex mb-2 text-md" htmlFor={id}>
				Mot de passe
			</Label>
			<div className="relative">
				<Input
					id={id}
					className="pe-9"
					placeholder="Password"
					type={isVisible ? "text" : "password"}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					aria-describedby={`${id}-description`}
				/>
				<button
					className="text-muted-foreground/80 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none focus:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
					type="button"
					onClick={toggleVisibility}
					aria-label={isVisible ? "Hide password" : "Show password"}
					aria-pressed={isVisible}
					aria-controls="password"
				>
					{isVisible ? (
						<EyeOffIcon size={16} aria-hidden="true" />
					) : (
						<EyeIcon size={16} aria-hidden="true" />
					)}
				</button>
			</div>

			<div
				className="bg-border mt-3 mb-4 h-1 w-full overflow-hidden rounded-full"
				role="progressbar"
				aria-valuenow={strengthScore}
				aria-valuemin={0}
				aria-valuemax={5}
				aria-label="Password strength"
				tabIndex={0}
			>
				<div
					className={`h-full ${getStrengthColor(strengthScore)} transition-all duration-500 ease-out`}
					style={{ width: `${(strengthScore / 5) * 100}%` }}
				/>
			</div>

			<p
				id={`${id}-description`}
				className="text-foreground mb-2 text-sm font-medium"
			>
				{getStrengthText(strengthScore)}. Doit contenir:
			</p>

			<ul className="space-y-1.5" aria-label="Password requirements">
				{strength.map((req) => (
					<li key={req.text} className="flex items-center gap-2">
						{req.met ? (
							<CheckIcon
								size={16}
								className="text-emerald-500"
								aria-hidden="true"
							/>
						) : (
							<XIcon
								size={16}
								className="text-muted-foreground/80"
								aria-hidden="true"
							/>
						)}
						<span
							className={`text-xs ${req.met ? "text-emerald-600" : "text-muted-foreground"}`}
						>
							{req.text}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

export default NewPasswordComponent;